import React, { useState, useEffect } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./utils/authContext";
import { getUserSettings, updateUserSettings } from "./utils/firestoreService";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./utils/firebase";
import { meetsMinRole } from "./utils/roleUtils";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import AdminUsers from "./admin/AdminUsers";
import AdminPrompts from "./admin/AdminPrompts";
import AdminCompanies from "./admin/AdminCompanies";
import AdminDocuments from "./admin/AdminDocuments";
import AdminStats from "./admin/AdminStats";
import AdminDataEditor from "./admin/AdminDataEditor";
import AdminSystemSettings from "./admin/AdminSystemSettings";
import { ADMIN_TABS, adminTabPath } from './admin/adminNavigation';

const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [adminPrefs, setAdminPrefs] = useState(null);
  const [hasUnsavedAdminChanges, setHasUnsavedAdminChanges] = useState(false);

  const routeTab = location.pathname.split('/')[2];
  const activeTab = ADMIN_TABS.findIndex(tab => tab.key === routeTab);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setAccessDenied(false);
    const checkAdmin = async () => {
      if (userId) {
        try {
          // Verify superAdmin role
          const userDoc = await getDoc(doc(db, "users", userId));
          if (cancelled) return;
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (!meetsMinRole(userData.userRole, 'superAdmin')) {
              console.warn("Access denied: User is not superAdmin");
              setAccessDenied(true);
              return;
            }
          } else {
            setAccessDenied(true);
            return;
          }
          
          // Load admin preferences from user settings
          const settings = await getUserSettings(userId);
          if (cancelled) return;
          const prefs = settings.adminPreferences || {};
          setAdminPrefs(prefs);
        } catch (error) {
          if (cancelled) return;
          console.error("Error verifying admin status:", error);
          setAccessDenied(true);
        }
      }
      if (!cancelled) setIsLoading(false);
    };

    checkAdmin();
    return () => { cancelled = true; };
  }, [userId]);

  const handleTabChange = (event, newValue) => {
    if (hasUnsavedAdminChanges && !window.confirm('You have unsaved changes. Discard them?')) return;
    setHasUnsavedAdminChanges(false);
    navigate(adminTabPath(newValue));
    // Save last tab to user settings
    if (userId) {
      const updatedPrefs = { ...adminPrefs, lastTab: newValue };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { adminPreferences: updatedPrefs })
        .catch(error => console.error('Unable to remember the Admin tab:', error));
    }
  };

  if (accessDenied) return <Navigate to="/dashboard" replace />;

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="header-container">
          <div className="page-title">Admin</div>
        </div>
      </div>
    );
  }

  if (activeTab === -1) {
    // The legacy entry point resumes the last tab; explicit URLs always win.
    const stateIndex = ADMIN_TABS.findIndex(tab => tab.key === location.state?.tab);
    const resumeIndex = !routeTab ? (stateIndex >= 0 ? stateIndex : adminPrefs?.lastTab) : 0;
    return <Navigate to={adminTabPath(resumeIndex)} replace />;
  }

  return (
    <div className="page-container" key={userId}>
      <div className="header-container" style={{ justifyContent: 'space-between' }}>
        <div className="page-title" style={{ marginBottom: 0 }}>Admin</div>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="admin tabs"
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 'auto' }}
        >
          {ADMIN_TABS.map(tab => <Tab key={tab.key} label={tab.label} sx={{ minHeight: 'auto', py: 1 }} />)}
        </Tabs>
      </div>

      {activeTab === 0 && <AdminStats adminPrefs={adminPrefs} setAdminPrefs={setAdminPrefs} userId={userId} />}
      {activeTab === 1 && <AdminCompanies adminPrefs={adminPrefs} setAdminPrefs={setAdminPrefs} userId={userId} />}
      {activeTab === 2 && <AdminUsers currentUser={currentUser} adminPrefs={adminPrefs} setAdminPrefs={setAdminPrefs} />}
      {activeTab === 3 && <AdminDocuments userId={userId} onDirtyChange={setHasUnsavedAdminChanges} />}
      {activeTab === 4 && <AdminPrompts adminPrefs={adminPrefs} setAdminPrefs={setAdminPrefs} userId={userId} />}
      {activeTab === 5 && <AdminDataEditor userId={userId} />}
      {activeTab === 6 && <AdminSystemSettings adminPrefs={adminPrefs} setAdminPrefs={setAdminPrefs} userId={userId} onDirtyChange={setHasUnsavedAdminChanges} />}
    </div>
  );
};

export default Admin;
